import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Text,
  Token,
  TransitionCollapse,
} from '@revolut-internal/ui-kit'
import { FC, PropsWithChildren, useState } from 'react'
import styled from 'styled-components'

const StyledBox = styled(Box)`
  & [role='button'] {
    cursor: pointer;
  }
`

type Props = PropsWithChildren<{
  collapsible: boolean
  label: string
  currentPage?: number
  totalPages?: number
  onPrevClick?: VoidFunction
  onNextClick?: VoidFunction
}>

export const CollapsibleChatMessage: FC<Props> = props => {
  const {
    children,
    collapsible,
    label,
    currentPage = 0,
    totalPages = 0,
    onPrevClick,
    onNextClick,
  } = props

  const [open, setOpen] = useState(true)
  const [transitionDuration, setTransitionDuration] = useState(0)

  const contentVisible = open || !collapsible
  const prevDisabled = currentPage <= 0
  const nextDisabled = currentPage >= totalPages - 1

  const handleToggleClick = () => {
    setTransitionDuration(300)
    setOpen(v => !v)
  }

  return (
    <StyledBox
      width="100%"
      px="s-16"
      py="s-8"
      borderRadius={Token.radius.widget}
      aria-expanded={open}
    >
      {collapsible && (
        <Flex
          pb={contentVisible ? 's-4' : undefined}
          gap="s-16"
          justifyContent="space-between"
        >
          <Flex
            gap="s-8"
            alignItems="center"
            onClick={handleToggleClick}
            role="button"
            aria-label={open ? 'Close' : 'Open'}
          >
            <Text variant="h6">{label}</Text>
            <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} />
          </Flex>
          {totalPages > 1 && (
            <HStack align="center" gap="s-4">
              <IconButton
                disabled={prevDisabled}
                useIcon="ChevronLeft"
                size={16}
                color={prevDisabled ? 'grey-tone-20' : 'grey-tone-50'}
                onClick={onPrevClick}
              >
                Prev
              </IconButton>
              <Text variant="h6" color="grey-tone-50">
                {currentPage + 1} / {totalPages}
              </Text>
              <IconButton
                disabled={nextDisabled}
                useIcon="ChevronRight"
                size={16}
                color={nextDisabled ? 'grey-tone-20' : 'grey-tone-50'}
                onClick={onNextClick}
              >
                Next
              </IconButton>
            </HStack>
          )}
        </Flex>
      )}
      <TransitionCollapse in={contentVisible} duration={transitionDuration}>
        {contentVisible && children}
      </TransitionCollapse>
    </StyledBox>
  )
}
